"use client";

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type RefObject,
} from "react";
import { useLocale, useNow, useTranslations } from "use-intl";

import { useAppDateTime } from "@/hooks/use-app-date-time";
import { formatPrice } from "@/lib/format-price";
import { cn } from "@/lib/utils";

import type { buildOfferHistoryChartModel } from "./buildOfferHistoryChartModel";
import type { OfferHistoryComparisonPoint } from "./offer-history-comparison";
import {
  availableWindows,
  bucketDailyPoints,
  priceAxisTicks,
  pricePosition,
  sliceHistoryWindow,
  windowExtremes,
  type HistoryWindowId,
} from "./offer-history-window";

type ChartModel = ReturnType<typeof buildOfferHistoryChartModel>;
type ChartScale = (value: number) => number;
type PriceKey = "minimum" | "selected";
type ChartSize = "compact" | "regular";
type PricedTime = { time: number; price: number };
type HistoryData = NonNullable<ReturnType<typeof historyData>>;
type PlotGeometry = ReturnType<typeof plotGeometry>;

const WINDOW_MESSAGE = { "30d": "days30", "90d": "days90", all: "other" } as const;
/** Reference deltas under half a percent read as "at the reference", not above or below it. */
const DELTA_THRESHOLD = 0.005;
const PLOT_KEYS = ["ArrowLeft", "ArrowRight", "Home", "End"];

/**
 * An offer's price history, recent days first: the y-axis always spans the listing's whole
 * history while the window switch only narrows the dates, so a short window never exaggerates
 * a move. One point per day; only the window's high, low and the current price are marked.
 */
export function OfferHistoryComparisonChart({
  model,
  currency,
  referencePrice,
  dealDeltaPercent,
  checkedAt,
  available,
  size = "regular",
}: {
  model: ChartModel;
  currency: string | null;
  referencePrice: string | null;
  dealDeltaPercent: string | null;
  checkedAt: Date | string | null;
  available: boolean;
  size?: ChartSize;
}) {
  const now = useNow().getTime();
  const { calendarDayKey } = useAppDateTime();
  const [choice, setChoice] = useState<HistoryWindowId | null>(null);
  const data = useMemo(
    () =>
      historyData({
        model,
        referencePrice,
        checkedAt,
        available,
        now,
        dayKey: calendarDayKey,
        choice,
      }),
    [model, referencePrice, checkedAt, available, now, calendarDayKey, choice],
  );
  if (!data) return null;

  return (
    <div className="min-w-0 space-y-2.5">
      {size === "regular" && (
        <HistoryHeadline
          data={data}
          available={available}
          currency={currency}
          dealDeltaPercent={dealDeltaPercent}
        />
      )}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <HistoryWindowSwitch windows={data.windows} window={data.window} onSelect={setChoice} />
        <HistoryLegend showReference={data.reference !== null} />
      </div>
      <HistoryPlot
        key={data.window}
        data={data}
        model={model}
        currency={currency}
        available={available}
        size={size}
      />
      <HistoryStats data={data} currency={currency} checkedAt={checkedAt} />
    </div>
  );
}

function historyData({
  model,
  referencePrice,
  checkedAt,
  available,
  now,
  dayKey,
  choice,
}: {
  model: ChartModel;
  referencePrice: string | null;
  checkedAt: Date | string | null;
  available: boolean;
  now: number;
  dayKey: (time: number) => string;
  choice: HistoryWindowId | null;
}) {
  const endTime = chartEndTime(model.points, checkedAt, available, now);
  const windows = pricedWindows(model.points, endTime);
  const window = pickWindow(windows, choice);
  const points = bucketDailyPoints(sliceHistoryWindow(model.points, window, endTime), dayKey);
  const reference = chartReference(model, referencePrice);
  const domain = [
    reference,
    ...model.points.flatMap((point) => [point.minimum, point.selected]),
  ].filter((value): value is number => value !== null);
  const lastPriced = points.findLast((point) => point.minimum !== null);
  if (!domain.length || !lastPriced) return null;
  return {
    window,
    windows,
    points,
    endTime,
    reference,
    lastPriced,
    current: lastPriced.minimum!,
    axis: priceAxisTicks(Math.min(...domain), Math.max(...domain)),
    extremes: windowExtremes(points),
  };
}

function pickWindow(windows: HistoryWindowId[], choice: HistoryWindowId | null) {
  if (choice && windows.includes(choice)) return choice;
  return windows[0] ?? "all";
}

function usePrice(currency: string | null) {
  const locale = useLocale();
  return (value: number) => formatPrice(value, currency ?? "BRL", locale);
}

function HistoryHeadline({
  data,
  available,
  currency,
  dealDeltaPercent,
}: {
  data: HistoryData;
  available: boolean;
  currency: string | null;
  dealDeltaPercent: string | null;
}) {
  const t = useTranslations("shared.affiliates");
  const locale = useLocale();
  const { monthDay } = useAppDateTime();
  const price = usePrice(currency);
  const delta = Number(dealDeltaPercent);
  return (
    <p className="text-[15px] leading-snug text-muted-foreground">
      {t.rich("historyHeadline", {
        state: available ? "available" : "unavailable",
        price: price(data.current),
        date: monthDay(data.lastPriced.time),
        direction: deltaDirection(available && data.reference !== null, delta),
        percent: new Intl.NumberFormat(locale, {
          style: "percent",
          maximumFractionDigits: 0,
        }).format(Math.abs(delta)),
        position: headlinePosition(data, available),
        window: WINDOW_MESSAGE[data.window],
        strong: (chunks) => <strong className="font-black text-foreground">{chunks}</strong>,
        down: (chunks) => <span className="font-bold text-emerald-400">{chunks}</span>,
        up: (chunks) => <span className="font-bold text-red-300">{chunks}</span>,
      })}
    </p>
  );
}

function deltaDirection(applies: boolean, delta: number) {
  if (!applies || !Number.isFinite(delta) || Math.abs(delta) < DELTA_THRESHOLD) return "none";
  return delta < 0 ? "below" : "above";
}

/** A window with a single price has no "near the lowest" to speak of. */
function headlinePosition(data: HistoryData, available: boolean) {
  const { low, high } = data.extremes;
  if (!available || !low || !high || low.price === high.price) return "none";
  return pricePosition(data.current, low.price, high.price);
}

function HistoryWindowSwitch({
  windows,
  window,
  onSelect,
}: {
  windows: HistoryWindowId[];
  window: HistoryWindowId;
  onSelect: (window: HistoryWindowId) => void;
}) {
  const t = useTranslations("shared.affiliates");
  if (windows.length < 2) return <span />;
  return (
    <div
      role="group"
      aria-label={t("historyWindowLabel")}
      className="inline-flex gap-0.5 rounded-lg border border-border bg-background/60 p-0.5"
    >
      {windows.map((id) => (
        <button
          key={id}
          type="button"
          aria-pressed={id === window}
          onClick={() => onSelect(id)}
          className={cn(
            "min-h-8 rounded-md px-3 text-xs font-bold transition-colors max-sm:min-h-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
            id === window
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {t("historyWindow", { window: WINDOW_MESSAGE[id] })}
        </button>
      ))}
    </div>
  );
}

function HistoryLegend({ showReference }: { showReference: boolean }) {
  const t = useTranslations("shared.affiliates");
  return (
    <span className="inline-flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
      <span className="inline-flex items-center gap-1.5">
        <span aria-hidden="true" className="size-2 rounded-full bg-red-400" />
        {t("historyLegend", { item: "max" })}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span aria-hidden="true" className="size-2 rounded-full bg-emerald-400" />
        {t("historyLegend", { item: "min" })}
      </span>
      {showReference && (
        <span className="inline-flex items-center gap-1.5">
          <svg aria-hidden="true" width="16" height="4">
            <line x1="0" x2="16" y1="2" y2="2" stroke="currentColor" strokeDasharray="2 3" />
          </svg>
          {t("historyLegend", { item: "reference" })}
        </span>
      )}
    </span>
  );
}

function HistoryPlot({
  data,
  model,
  currency,
  available,
  size,
}: {
  data: HistoryData;
  model: ChartModel;
  currency: string | null;
  available: boolean;
  size: ChartSize;
}) {
  const t = useTranslations("shared.affiliates");
  const locale = useLocale();
  const { monthDay } = useAppDateTime();
  const price = usePrice(currency);
  const [svgRef, width] = useElementWidth(size === "compact" ? 560 : 640);
  const axisFormat = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency ?? "BRL",
    maximumFractionDigits: 0,
  });
  const geo = plotGeometry(data, width, size, axisFormat);
  const { selected, handlers } = usePlotSelection(data.points, (clientX) => {
    const rect = svgRef.current!.getBoundingClientRect();
    return nearestPoint(data.points, ((clientX - rect.left) / rect.width) * width, geo.x);
  });
  const nowX = available ? geo.x(data.endTime) : geo.x(data.lastPriced.time);

  return (
    <div className="relative">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${geo.height}`}
        className="block w-full touch-pan-y rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        role="img"
        aria-label={t("priceHistory")}
        tabIndex={0}
        {...handlers}
      >
        <title>{t("priceHistory")}</title>
        <desc>
          {t("historyChartDescription", {
            selected: model.showSelected ? "yes" : "no",
            listing: model.hasListingHistory ? "yes" : "no",
          })}
        </desc>
        <PlotGrid data={data} geo={geo} format={(value) => axisFormat.format(value)} />
        {model.showSelected && (
          <path
            d={stepPath(data.points, "selected", geo.x, geo.y, nowX)}
            stroke="var(--chart-2)"
            strokeDasharray="5 4"
            strokeWidth="2"
            fill="none"
          />
        )}
        <path
          d={stepPath(data.points, "minimum", geo.x, geo.y, nowX)}
          stroke="var(--chart-1)"
          strokeWidth="2.25"
          strokeLinejoin="round"
          fill="none"
        />
        {selected && (
          <line
            x1={geo.x(selected.time)}
            x2={geo.x(selected.time)}
            y1={geo.top}
            y2={geo.bottom}
            stroke="var(--muted-foreground)"
            strokeDasharray="3 4"
            opacity="0.5"
            pointerEvents="none"
          />
        )}
        <PlotMarkers data={data} geo={geo} nowX={nowX} price={price} />
        <text x={geo.left} y={geo.height - 5} className="fill-muted-foreground text-xs">
          {monthDay(geo.firstTime)}
        </text>
        <text
          x={geo.right}
          y={geo.height - 5}
          textAnchor="end"
          className="fill-muted-foreground text-xs"
        >
          {available ? t("historyToday") : monthDay(data.endTime)}
        </text>
      </svg>
      {selected && <PlotReadout point={selected} price={price} />}
    </div>
  );
}

function PlotReadout({
  point,
  price,
}: {
  point: OfferHistoryComparisonPoint;
  price: (value: number) => string;
}) {
  const t = useTranslations("shared.affiliates");
  const { monthDay } = useAppDateTime();
  return (
    <p className="pointer-events-none absolute right-0 top-0 rounded bg-card/90 px-1.5 text-xs font-semibold tabular-nums">
      {t("historyActivePoint", {
        date: monthDay(point.time),
        price: point.minimum === null ? "—" : price(point.minimum),
      })}
    </p>
  );
}

function plotGeometry(
  data: HistoryData,
  width: number,
  size: ChartSize,
  axisFormat: Intl.NumberFormat,
) {
  const height = size === "compact" ? 148 : Math.round(Math.max(180, Math.min(240, width * 0.32)));
  const left =
    Math.max(...data.axis.ticks.map((tick) => axisFormat.format(tick).length)) * 7.5 + 10;
  const right = width - 8;
  const top = 22;
  const bottom = height - 26;
  const firstTime = data.points[0]!.time;
  const span = data.endTime - firstTime;
  const { min, max } = data.axis;
  return {
    height,
    left,
    right,
    top,
    bottom,
    firstTime,
    x: (time: number) =>
      span <= 0 ? (left + right) / 2 : left + ((time - firstTime) / span) * (right - left),
    y: (value: number) => bottom - ((value - min) / (max - min)) * (bottom - top),
  };
}

function PlotGrid({
  data,
  geo,
  format,
}: {
  data: HistoryData;
  geo: PlotGeometry;
  format: (value: number) => string;
}) {
  return (
    <>
      {data.axis.ticks.map((tick) => (
        <g key={tick}>
          <line
            x1={geo.left}
            x2={geo.right}
            y1={geo.y(tick)}
            y2={geo.y(tick)}
            stroke="var(--border)"
          />
          <text
            data-axis-price
            x={geo.left - 8}
            y={geo.y(tick) + 4}
            textAnchor="end"
            className="fill-muted-foreground text-xs"
          >
            {format(tick)}
          </text>
        </g>
      ))}
      {data.reference !== null && (
        <line
          x1={geo.left}
          x2={geo.right}
          y1={geo.y(data.reference)}
          y2={geo.y(data.reference)}
          stroke="var(--muted-foreground)"
          strokeDasharray="2 4"
        />
      )}
    </>
  );
}

function PlotMarkers({
  data,
  geo,
  nowX,
  price,
}: {
  data: HistoryData;
  geo: PlotGeometry;
  nowX: number;
  price: (value: number) => string;
}) {
  const high = visibleExtreme(data.extremes.high, data);
  const low = visibleExtreme(data.extremes.low, data);
  const nowY = geo.y(data.current);
  return (
    <>
      {high && (
        <ExtremeMarker
          kind="max"
          cx={geo.x(high.time)}
          cy={geo.y(high.price)}
          label={price(high.price)}
          labelY={Math.max(13, geo.y(high.price) - 10)}
          anchor={labelAnchor(geo.x(high.time), geo.left, geo.right)}
        />
      )}
      {low && (
        <ExtremeMarker
          kind="min"
          cx={geo.x(low.time)}
          cy={geo.y(low.price)}
          label={price(low.price)}
          labelY={Math.min(geo.bottom - 2, geo.y(low.price) + 18)}
          anchor={labelAnchor(geo.x(low.time), geo.left, geo.right)}
        />
      )}
      <circle
        data-marker="now"
        cx={nowX}
        cy={nowY}
        r="5"
        fill="var(--chart-1)"
        stroke="var(--card)"
        strokeWidth="2"
      />
      <text
        x={nowX - 8}
        y={nowY - geo.top > 18 ? nowY - 10 : nowY + 19}
        textAnchor="end"
        className="fill-foreground text-xs font-extrabold"
        stroke="var(--card)"
        strokeWidth="3"
        paintOrder="stroke"
      >
        {price(data.current)}
      </text>
    </>
  );
}

/** The window's high or low, unless the window is flat or that point is the current price. */
function visibleExtreme(item: PricedTime | null, data: HistoryData) {
  const { low, high } = data.extremes;
  if (!item || !low || !high || low.price === high.price) return null;
  const isCurrent = item.time === data.lastPriced.time && item.price === data.current;
  return isCurrent ? null : item;
}

function ExtremeMarker({
  kind,
  cx,
  cy,
  label,
  labelY,
  anchor,
}: {
  kind: "max" | "min";
  cx: number;
  cy: number;
  label: string;
  labelY: number;
  anchor: "start" | "middle" | "end";
}) {
  return (
    <g pointerEvents="none">
      <circle
        data-marker={kind}
        className={kind === "max" ? "fill-red-400" : "fill-emerald-400"}
        cx={cx}
        cy={cy}
        r="4.5"
        stroke="var(--card)"
        strokeWidth="1.5"
      />
      <text
        x={cx}
        y={labelY}
        textAnchor={anchor}
        className={cn(
          "text-xs font-extrabold",
          kind === "max" ? "fill-red-300" : "fill-emerald-400",
        )}
        stroke="var(--card)"
        strokeWidth="3"
        paintOrder="stroke"
      >
        {label}
      </text>
    </g>
  );
}

function HistoryStats({
  data,
  currency,
  checkedAt,
}: {
  data: HistoryData;
  currency: string | null;
  checkedAt: Date | string | null;
}) {
  const t = useTranslations("shared.affiliates");
  const { monthDay, recentTime } = useAppDateTime();
  const price = usePrice(currency);
  const stats = [
    ["min", data.extremes.low?.price ?? null, "text-emerald-400"],
    ["average", data.extremes.average, "text-foreground"],
    ["max", data.extremes.high?.price ?? null, "text-red-300"],
  ] as const;
  return (
    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-t border-border/60 pt-2.5 text-sm tabular-nums text-muted-foreground">
      <span>
        {t("historyStatsScope", {
          window: WINDOW_MESSAGE[data.window],
          date: monthDay(data.points[0]!.time),
        })}
      </span>
      {stats.map(([stat, value, tone]) => (
        <span key={stat}>
          {t("historySummaryLabel", { stat })}{" "}
          <b className={cn("font-semibold", tone)}>{value === null ? "—" : price(value)}</b>
        </span>
      ))}
      {checkedAt && <span className="ml-auto">{recentTime(checkedAt)?.value}</span>}
    </div>
  );
}

function useElementWidth(initial: number): [RefObject<SVGSVGElement | null>, number] {
  const ref = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(initial);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry && entry.contentRect.width > 0) setWidth(Math.round(entry.contentRect.width));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

/** Hover follows the mouse; a click, touch drag or arrow key pins a day until cleared. */
function usePlotSelection(
  points: OfferHistoryComparisonPoint[],
  locate: (clientX: number) => number,
) {
  const [active, setActive] = useState<number | null>(null);
  const [pinned, setPinned] = useState<number | null>(null);
  const index = active ?? pinned;
  const pin = (next: number | null) => {
    setActive(next);
    setPinned(next);
  };
  const handlers = {
    onPointerMove: (event: PointerEvent<SVGSVGElement>) => {
      if (event.pointerType !== "mouse") {
        if (event.buttons === 1) pin(locate(event.clientX));
        return;
      }
      if (pinned === null) setActive(locate(event.clientX));
    },
    onPointerDown: (event: PointerEvent<SVGSVGElement>) => {
      const next = locate(event.clientX);
      pin(pinned === next ? null : next);
    },
    onPointerLeave: () => {
      if (pinned === null) setActive(null);
    },
    onKeyDown: (event: KeyboardEvent<SVGSVGElement>) => {
      if (event.key === "Escape") return pin(null);
      if (!PLOT_KEYS.includes(event.key)) return;
      event.preventDefault();
      pin(keyboardIndex(event.key, index ?? points.length - 1, points.length));
    },
  };
  return { selected: index === null ? null : (points[index] ?? null), handlers };
}

/** Available offers run to now; otherwise the chart ends at the last check. */
function chartEndTime(
  points: OfferHistoryComparisonPoint[],
  checkedAt: Date | string | null,
  available: boolean,
  now: number,
) {
  const lastPoint = points.at(-1)?.time ?? now;
  if (available) return Math.max(now, lastPoint);
  const checked = checkedAt === null ? NaN : new Date(checkedAt).getTime();
  return Number.isFinite(checked) ? Math.max(checked, lastPoint) : lastPoint;
}

/** Windows that still show a price, so a long-unavailable offer never opens or switches to an empty chart. */
function pricedWindows(points: OfferHistoryComparisonPoint[], endTime: number) {
  return availableWindows(points, endTime).filter((id) =>
    sliceHistoryWindow(points, id, endTime).some((point) => point.minimum !== null),
  );
}

function chartReference(model: ChartModel, referencePrice: string | null) {
  if (!model.hasListingHistory || referencePrice === null) return null;
  const value = Number(referencePrice);
  return Number.isFinite(value) ? value : null;
}

/** Step line through daily prices; unavailable days break the line. */
function stepPath(
  points: OfferHistoryComparisonPoint[],
  key: PriceKey,
  x: ChartScale,
  y: ChartScale,
  endX: number,
) {
  return points
    .map((point, index) => {
      const value = point[key];
      if (value === null) return "";
      const next = points[index + 1];
      const toX = next ? x(next.time) : endX;
      const continues = index > 0 && points[index - 1]![key] !== null;
      return `${continues ? `V${y(value)}` : `M${x(point.time)},${y(value)}`}H${toX}`;
    })
    .join("");
}

function labelAnchor(cx: number, left: number, right: number) {
  if (cx < left + 32) return "start";
  if (cx > right - 32) return "end";
  return "middle";
}

function nearestPoint(points: OfferHistoryComparisonPoint[], target: number, x: ChartScale) {
  return points.reduce(
    (nearest, point, index) =>
      Math.abs(x(point.time) - target) < Math.abs(x(points[nearest]!.time) - target)
        ? index
        : nearest,
    0,
  );
}

function keyboardIndex(key: string, current: number, count: number) {
  if (key === "Home") return 0;
  if (key === "End") return count - 1;
  return Math.max(0, Math.min(count - 1, current + (key === "ArrowLeft" ? -1 : 1)));
}
